import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IonButton, IonIcon } from '@ionic/react';
import { contractOutline, expandOutline, navigateOutline } from 'ionicons/icons';
import L from 'leaflet';
import { baseTileLayer, directLine, fitToPoints, markerIcon, navigationUrl } from '../lib/leaflet';
import { BACK_PRIORITY, useBackButton } from '../lib/backbutton';
import type { Ride } from '../lib/types';

interface Props {
  ride: Ride;
  tall?: boolean;
  /** Blendet "Route öffnen" ein - nur für den Fahrer sinnvoll. */
  canNavigate?: boolean;
}

/** Zeichnet den Zustand einer Fahrt auf eine eigene Leaflet-Karte. */
function MapCanvas({
  ride,
  className,
  withZoom = false,
}: {
  ride: Ride;
  className: string;
  /** Zoomknöpfe nur im Vollbild: auf der eingebetteten 260px-Karte
      verdecken sie fast die Hälfte, und auf Touch zoomt man ohnehin
      mit zwei Fingern. */
  withZoom?: boolean;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const hasFitted = useRef(false);

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, { zoomControl: false }).setView([51.1657, 10.4515], 5);
    baseTileLayer().addTo(instance);
    if (withZoom) L.control.zoom({ position: 'bottomright' }).addTo(instance);
    layer.current = L.layerGroup().addTo(instance);
    map.current = instance;

    const timer = window.setTimeout(() => instance.invalidateSize(), 250);
    return () => {
      window.clearTimeout(timer);
      instance.remove();
      map.current = null;
      layer.current = null;
    };
  }, [withZoom]);

  useEffect(() => {
    const instance = map.current;
    const group = layer.current;
    if (!instance || !group) return;

    group.clearLayers();
    const points: L.LatLngExpression[] = [];

    if (ride.origin) {
      const origin: L.LatLngExpression = [ride.origin.lat, ride.origin.lng];
      L.marker(origin, { icon: markerIcon('origin') })
        .bindTooltip('Startpunkt', { direction: 'top' })
        .addTo(group);
      // Der Radius, ab dem "losgefahren" gemeldet wird - macht die Regel sichtbar.
      L.circle(origin, {
        radius: ride.settings.departureRadiusM,
        color: '#8b93a3',
        weight: 1,
        fillColor: '#8b93a3',
        fillOpacity: 0.12,
      }).addTo(group);
      points.push(origin);
    }

    if (ride.pickup) {
      const pickup: L.LatLngExpression = [ride.pickup.lat, ride.pickup.lng];
      L.marker(pickup, { icon: markerIcon('pickup') })
        .bindTooltip(ride.pickup.label ?? 'Abholpunkt', { direction: 'top' })
        .addTo(group);
      points.push(pickup);

      // Stehen beide Enden fest, die Verbindung zeigen - ab Fahrtbeginn von
      // der aktuellen Position aus, vorher vom Startpunkt.
      const from: L.LatLngExpression | null = ride.driver
        ? [ride.driver.lat, ride.driver.lng]
        : ride.origin
          ? [ride.origin.lat, ride.origin.lng]
          : null;
      if (from) {
        directLine(from, [ride.pickup.lat, ride.pickup.lng])
          .bindTooltip('Luftlinie - die gefahrene Route zeigt Google Maps', { sticky: true })
          .addTo(group);
      }
    }

    if (ride.track.length > 1) {
      L.polyline(
        ride.track.map((point) => [point.lat, point.lng] as L.LatLngExpression),
        // Der gefahrene Weg ist das Einzige auf der Karte, das die
        // Akzentfarbe tragen darf - er gehört zum Fahrer.
        { color: '#12c46b', weight: 5, opacity: 0.9, lineCap: 'round' },
      ).addTo(group);
    }

    if (ride.driver) {
      const driver: L.LatLngExpression = [ride.driver.lat, ride.driver.lng];
      L.marker(driver, { icon: markerIcon('driver'), zIndexOffset: 500 })
        .bindTooltip(ride.driverName, { direction: 'top' })
        .addTo(group);
      points.push(driver);
    }

    // Nur beim ersten Mal automatisch einpassen - danach darf der Nutzer
    // frei zoomen, ohne dass die Karte alle 15 Sekunden zurückspringt.
    if (!hasFitted.current && points.length > 0) {
      fitToPoints(instance, points);
      hasFitted.current = true;
    }
  }, [ride]);

  return <div ref={container} className={className} />;
}

/**
 * Verfolgt den Fahrer: Startpunkt, gefahrene Linie, aktuelle Position und
 * Abholpunkt.
 *
 * Der Vollbildmodus hängt bewusst als Portal am `body`: `position: fixed`
 * wird von jedem Vorfahren mit `transform`, `filter` oder `backdrop-filter`
 * eingefangen und füllt dann nur die Karte statt den Bildschirm. Genau das
 * ist hier schon einmal passiert.
 */
export function RideMap({ ride, tall = true, canNavigate = false }: Props) {
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    // Solange die Karte den Schirm füllt, soll die Seite darunter nicht scrollen.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [fullscreen]);

  // Die Zurücktaste schliesst erst das Vollbild - wie Escape am Rechner -
  // bevor sie eine Ansicht zurückgeht.
  useBackButton(BACK_PRIORITY.overlay, (next) => {
    if (fullscreen) setFullscreen(false);
    else next();
  });

  const navigate = () => {
    if (!ride.pickup) return;
    window.open(navigationUrl(ride.driver ?? ride.origin ?? null, ride.pickup), '_blank', 'noopener');
  };

  // Eine Reihe, oben rechts: "Route öffnen" links, das Vollbild-Symbol ganz
  // aussen. Der Knopf mit Text steht damit weiter in der Mitte, wo der Daumen
  // ihn eher trifft - und die Ecke gehoert dem, was auf jeder Karte gleich
  // liegt.
  const actions = (expanded: boolean) => (
    <div className="r2f-map__actions">
      {canNavigate && ride.pickup && (
        <IonButton size="small" fill="solid" onClick={navigate}>
          <IonIcon slot="start" icon={navigateOutline} />
          Route öffnen
        </IonButton>
      )}

      <IonButton
        size="small"
        fill="solid"
        className="r2f-mapbtn"
        onClick={() => setFullscreen(!expanded)}
        aria-label={expanded ? 'Vollbild beenden' : 'Karte im Vollbild'}
      >
        <IonIcon slot="icon-only" icon={expanded ? contractOutline : expandOutline} />
      </IonButton>
    </div>
  );

  return (
    <>
      <div className="r2f-mapwrap">
        <MapCanvas ride={ride} className={tall ? 'r2f-map r2f-map--tall' : 'r2f-map'} />
        {actions(false)}
      </div>

      {fullscreen &&
        createPortal(
          <div className="r2f-mapfull">
            <MapCanvas ride={ride} className="r2f-map r2f-map--full" withZoom />
            {actions(true)}
          </div>,
          document.body,
        )}
    </>
  );
}
